import React from 'react';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };

const SOCIAL = [
  { label: 'Instagram', short: 'IG',  url: 'https://www.instagram.com/tsanga_music/' },
  { label: 'SoundCloud', short: 'SC', url: 'https://soundcloud.com/tsanga-berlin' },
  { label: 'Bandcamp',  short: 'BC',  url: 'https://tsanga.bandcamp.com/album/a-piece-of-sky' },
  { label: 'Spotify',   short: 'SP',  url: 'https://open.spotify.com/intl-fr/artist/6SAdpFlN4N8Wg4UNcCIEyz' },
];

const NAV = [
  { id: 'hero',    n: 'I',   label: 'Tsanga' },
  { id: 'gallery', n: 'II',  label: 'Stills' },
  { id: 'record',  n: 'III', label: 'Music' },
  { id: 'lives',   n: 'IV',  label: 'Lives' },
  { id: 'shop',    n: 'V',   label: 'Merch' },
  { id: 'contact', n: 'VI',  label: 'Contact' },
];

const MONO = 'var(--mono)';

const labelStyle = {
  fontFamily: MONO,
  fontSize: 10,
  letterSpacing: '0.28em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.38)',
  marginBottom: 18,
  display: 'block',
};

const linkStyle = {
  fontFamily: MONO,
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.72)',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 10,
  padding: '4px 0',
  transition: 'color 160ms ease',
};

export default function Footer() {
  const { lang } = useLang();
  const t = T[lang]?.footer || T.fr.footer;

  const onJump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <footer
      role="contentinfo"
      style={{
        position: 'relative',
        zIndex: 5,
        background: 'transparent',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: 'clamp(56px, 7vw, 96px) clamp(20px, 4vw, 56px) clamp(120px, 14vw, 140px)',
      }}
    >
      <div className="noise" aria-hidden="true" />

      {/* Top grid */}
      <div
        className="footer-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(0, 1fr))',
          gap: 'clamp(32px, 4vw, 64px)',
          alignItems: 'start',
        }}
      >
        {/* Logo + tagline */}
        <div>
          <img
            src="/tsangalogosansfond.png"
            alt="Tsanga"
            style={{
              display: 'block',
              width: 'clamp(180px, 22vw, 320px)',
              height: 'auto',
              mixBlendMode: 'screen',
              opacity: 0.92,
            }}
          />
          <div
            style={{
              marginTop: 22,
              maxWidth: 360,
              fontFamily: MONO,
              fontSize: 11,
              lineHeight: 1.7,
              letterSpacing: '0.06em',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            {t.tagline || 'Architecte du vide sonore.\nÉlectronique, dark wave — Bruxelles.'}
          </div>
        </div>

        {/* Navigate */}
        <nav aria-label={t.navLabel || 'Site navigation'}>
          <span style={labelStyle}>{t.navHeading || 'Navigate'}</span>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {NAV.map(({ id, n, label }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={onJump(id)}
                  style={linkStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.72)')}
                >
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', minWidth: 18 }}>{n}</span>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Listen / Social */}
        <div>
          <span style={labelStyle}>{t.listenHeading || 'Listen'}</span>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {SOCIAL.map(({ label, short, url }) => (
              <li key={label}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.72)')}
                >
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', minWidth: 18 }}>{short}</span>
                  {label}
                  <span aria-hidden="true" style={{ marginLeft: 6, opacity: 0.4 }}>↗</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Studio / Booking */}
        <div>
          <span style={labelStyle}>{t.studioHeading || 'Studio'}</span>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 11,
              lineHeight: 1.9,
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
            }}
          >
            <div>Schaerbeek</div>
            <div>1030 Bruxelles</div>
            <div style={{ marginTop: 14, color: 'rgba(255,255,255,0.45)' }}>CSC Records</div>
          </div>
          <a
            href="#contact"
            onClick={onJump('contact')}
            style={{
              ...linkStyle,
              marginTop: 22,
              borderTop: '1px solid rgba(255,255,255,0.12)',
              paddingTop: 18,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.72)')}
          >
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>→</span>
            {t.bookingCta || 'Booking inquiry'}
          </a>
        </div>
      </div>

      {/* Bottom strip */}
      <div
        className="footer-bottom"
        style={{
          marginTop: 'clamp(56px, 7vw, 88px)',
          paddingTop: 22,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.32)',
        }}
      >
        <span>{t.copyright}</span>
        <span>{t.builtWith || 'Built quietly in Brussels'}</span>
        <a
          href="#privacy"
          style={{
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            padding: '6px 0',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
        >
          {t.privacy} →
        </a>
      </div>
    </footer>
  );
}
